// FILE: apps/admin/src/components/DailyStatsChart.tsx
// VERSION: 2.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Interactive SVG daily time-series area/line chart in MarketPlace purple style for Admin Dashboard analytics.
//   SCOPE: DailyStatsChart component rendering daily series for orders, revenue, Stars, net profit, and new users.
//   DEPENDS: ../api.js, ../display-currency.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   DailyStatPoint - Single day data point for DailyStatsChart
//   DailyStatsChart - Interactive purple SVG area/line chart component with metric and period selectors
// END_MODULE_MAP

import { useState } from 'react';

import { num } from '../api.js';
import { useDisplayCurrency } from '../display-currency.js';

export interface DailyStatPoint {
  day: string;
  ordersCount: number;
  deliveredOrdersCount: number;
  starsSold: number;
  premiumMonthsSold: number;
  grossRevenueKopecks: number;
  netProfitKopecks: number;
  newUsersCount: number;
  crashBetsKopecks: number;
  casesRevenueKopecks: number;
}

type MetricKey =
  | 'deliveredOrdersCount'
  | 'grossRevenueKopecks'
  | 'starsSold'
  | 'netProfitKopecks'
  | 'newUsersCount';

function formatRuShortDay(isoDay: string): string {
  // START_BLOCK_DAILY_STATS_CHART_FORMAT_DAY
  const d = new Date(`${isoDay}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDay.slice(5);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '');
  // END_BLOCK_DAILY_STATS_CHART_FORMAT_DAY
}

function formatAxisTick(v: number, isMoney: boolean): string {
  // START_BLOCK_DAILY_STATS_CHART_FORMAT_TICK
  const val = isMoney ? Math.round(v / 100) : Math.round(v);
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(val);
  // END_BLOCK_DAILY_STATS_CHART_FORMAT_TICK
}

// START_CONTRACT: DailyStatsChart
//   PURPOSE: Render an interactive purple SVG area and line chart with metric selector for daily operational metrics.
//   INPUTS: series array of DailyStatPoint, optional days and onChangeDays callback
//   OUTPUTS: React element
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: DailyStatsChart
export function DailyStatsChart({
  series,
  days,
  onChangeDays,
}: {
  series: DailyStatPoint[];
  days?: 7 | 14 | 30;
  onChangeDays?: (d: 7 | 14 | 30) => void;
}) {
  // START_BLOCK_DAILY_STATS_CHART
  const [metric, setMetric] = useState<MetricKey>('deliveredOrdersCount');
  const { money } = useDisplayCurrency();

  const tabs: Array<{ key: MetricKey; label: string; isMoney: boolean }> = [
    { key: 'deliveredOrdersCount', label: 'Динамика заказов', isMoney: false },
    { key: 'grossRevenueKopecks', label: 'Оборот', isMoney: true },
    { key: 'starsSold', label: 'Telegram Stars', isMoney: false },
    { key: 'netProfitKopecks', label: 'Чистая маржа', isMoney: true },
    { key: 'newUsersCount', label: 'Игроки', isMoney: false },
  ];

  const activeTab = tabs.find((t) => t.key === metric) ?? tabs[0]!;
  const points = series.length > 0 ? series : [];
  const rawValues = points.map((p) => {
    if (metric === 'deliveredOrdersCount') {
      return Math.max(0, Number(p.ordersCount || p.deliveredOrdersCount || 0));
    }
    return Math.max(0, Number(p[metric] ?? 0));
  });
  const maxVal = Math.max(4, ...rawValues);

  const W = 720;
  const H = 215;
  const padL = 42;
  const padR = 16;
  const padT = 14;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const coords = points.map((pt, idx) => {
    const val = rawValues[idx] ?? 0;
    const x =
      points.length <= 1
        ? padL + plotW / 2
        : padL + (idx / (points.length - 1)) * plotW;
    const y = padT + plotH - (val / maxVal) * plotH;
    return { x, y, val, day: pt.day };
  });

  const linePath = coords
    .map((c, idx) => `${idx === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`)
    .join(' ');
  const areaPath =
    coords.length > 0
      ? `${linePath} L${coords[coords.length - 1]!.x.toFixed(1)},${(padT + plotH).toFixed(1)} L${coords[0]!.x.toFixed(1)},${(padT + plotH).toFixed(1)} Z`
      : '';

  const yTicks = [1, 0.75, 0.5, 0.25, 0];
  const labelStride = points.length > 10 ? Math.ceil(points.length / 7) : 1;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Динамика показателей</div>

        <div className="row" style={{ gap: 8 }}>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as MetricKey)}
            aria-label="Показатель графика"
            className="chart-select"
          >
            {tabs.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>

          {onChangeDays && days && (
            <select
              value={days}
              onChange={(e) => onChangeDays(Number(e.target.value) as 7 | 14 | 30)}
              aria-label="Период графика"
              className="chart-select"
            >
              <option value={7}>7 дней</option>
              <option value={14}>14 дней</option>
              <option value={30}>30 дней</option>
            </select>
          )}
        </div>
      </div>

      {!coords.length ? (
        <div className="muted" style={{ padding: 36, textAlign: 'center', flex: 1, display: 'grid', placeItems: 'center' }}>
          Нет данных за выбранный период
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 190 }}>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            style={{ width: '100%', height: 'auto', overflow: 'visible', display: 'block' }}
          >
          <defs>
            <linearGradient id="mpPurpleArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8294ff" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#8294ff" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {yTicks.map((ratio) => {
            const y = padT + plotH * (1 - ratio);
            const tickVal = maxVal * ratio;
            return (
              <g key={ratio}>
                <line
                  x1={padL}
                  y1={y}
                  x2={W - padR}
                  y2={y}
                  stroke="rgba(186, 204, 235, 0.1)"
                  strokeWidth="1"
                />
                <text
                  x={padL - 8}
                  y={y + 4}
                  textAnchor="end"
                  fill="#6e819e"
                  fontSize="10.5"
                >
                  {formatAxisTick(tickVal, activeTab.isMoney)}
                </text>
              </g>
            );
          })}

          <path d={areaPath} fill="url(#mpPurpleArea)" />
          <path
            d={linePath}
            fill="none"
            stroke="#8294ff"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {coords.map((c, idx) => {
            const formatted = activeTab.isMoney ? money(c.val) : num(c.val);
            const showLabel = idx % labelStride === 0 || idx === coords.length - 1;
            return (
              <g key={c.day}>
                <circle
                  cx={c.x}
                  cy={c.y}
                  r="3.6"
                  fill="#a5b4fc"
                  stroke="#1e2738"
                  strokeWidth="1.5"
                >
                  <title>{`${c.day}: ${formatted}`}</title>
                </circle>
                {showLabel && (
                  <text
                    x={c.x}
                    y={H - 7}
                    textAnchor="middle"
                    fill="#6e819e"
                    fontSize="10.5"
                  >
                    {formatRuShortDay(c.day)}
                  </text>
                )}
              </g>
            );
          })}
          </svg>
        </div>
      )}
    </div>
  );
  // END_BLOCK_DAILY_STATS_CHART
}
